/**
 * Comparadores tipados para ordenação client-side de tabelas.
 *
 * AF-3418: existiam três comparadores espalhados pelo app, cada um com um defeito:
 *  - useVerificationConfigPageView: String(va).localeCompare(String(vb), {numeric:true}) —
 *    compara data como texto, então "02/01/2026" vem antes de "10/12/2025".
 *  - useDraweeConfirmationPageView: switch com localeCompare, sem nenhum ramo de data.
 *  - RecommendationEngineParecerView: parseBrazilianDate correto, mas fixo em DESC e
 *    redeclarado dentro do próprio sort.
 *
 * Este módulo centraliza o comportamento correto para os três tipos.
 */

export type SortOrder = 'ASC' | 'DESC';
export type SortValueType = 'text' | 'number' | 'date';

/**
 * Converte para timestamp aceitando os formatos que circulam no app:
 *  - BR: dd/MM/yyyy, dd/MM/yyyy HH:mm, dd/MM/yyyy HH:mm:ss
 *  - ISO: yyyy-MM-dd, yyyy-MM-ddTHH:mm:ss(.sss)(Z|±hh:mm)
 *
 * Retorna null quando não é uma data reconhecível — quem chama decide onde
 * posicionar o valor ausente (ver compareValues).
 */
export const parseDateValue = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value.getTime();
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }

    const raw = String(value).trim();
    if (!raw) return null;

    // Formato brasileiro: dd/MM/yyyy [HH:mm[:ss]]
    const br = raw.match(
        /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (br) {
        const [, day, month, year, hours, minutes, seconds] = br;
        const parsed = new Date(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hours ?? 0),
            Number(minutes ?? 0),
            Number(seconds ?? 0)
        );
        return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
    }

    // ISO (e qualquer coisa que o Date entenda nativamente)
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        const parsed = new Date(raw);
        return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
    }

    return null;
};

/** Extrai número de strings tolerando formato BR ("R$ 1.234,56", "2,40"). */
export const parseNumberValue = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'boolean') return value ? 1 : 0;

    let text = String(value).trim();
    if (!text) return null;

    text = text.replace(/^R\$\s*/i, '');

    const hasDot = text.includes('.');
    const hasComma = text.includes(',');
    if (hasDot && hasComma) {
        // Se a vírgula vem depois do ponto é formato BR (1.234,56)
        text = text.lastIndexOf(',') > text.lastIndexOf('.')
            ? text.replace(/\./g, '').replace(',', '.')
            : text.replace(/,/g, '');
    } else if (hasComma) {
        text = text.replace(',', '.');
    }

    const cleaned = text.replace(/[^\d.+-]/g, '');
    if (!cleaned || cleaned === '-' || cleaned === '+') return null;

    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
};

const isEmpty = (value: unknown): boolean =>
    value === null || value === undefined || value === '';

/**
 * Compara dois valores conforme o tipo declarado da coluna.
 *
 * Valores ausentes (null/undefined/'') vão SEMPRE para o fim, independente da direção.
 * Esse é o mesmo problema do `ORDER BY ... DESC` no Postgres, que joga NULL para o topo:
 * ao inverter a ordem o usuário recebia uma página cheia de células vazias e concluía
 * que a ordenação não funcionava.
 */
export const compareValues = (
    a: unknown,
    b: unknown,
    type: SortValueType = 'text',
    order: SortOrder = 'ASC'
): number => {
    const direction = order === 'ASC' ? 1 : -1;

    const aEmpty = isEmpty(a);
    const bEmpty = isEmpty(b);
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    if (type === 'date') {
        const da = parseDateValue(a);
        const db = parseDateValue(b);
        if (da === null && db === null) return 0;
        if (da === null) return 1;
        if (db === null) return -1;
        return (da - db) * direction;
    }

    if (type === 'number') {
        const na = parseNumberValue(a);
        const nb = parseNumberValue(b);
        if (na === null && nb === null) return 0;
        if (na === null) return 1;
        if (nb === null) return -1;
        return (na - nb) * direction;
    }

    return (
        String(a).localeCompare(String(b), 'pt-BR', {
            numeric: true,
            sensitivity: 'base',
        }) * direction
    );
};

/**
 * Ordena uma cópia da lista. Não muta o array recebido — importante porque estas listas
 * vêm direto do estado do React.
 */
export const sortRows = <T>(
    rows: T[],
    field: string | null | undefined,
    order: SortOrder,
    typeByField: Record<string, SortValueType> = {},
    valueAccessor?: (row: T, field: string) => unknown
): T[] => {
    if (!field) return rows;

    const type = typeByField[field] ?? 'text';
    const getValue = valueAccessor
        ? valueAccessor
        : (row: T, key: string) => (row as Record<string, unknown>)[key];

    return [...rows].sort((rowA, rowB) =>
        compareValues(getValue(rowA, field), getValue(rowB, field), type, order)
    );
};
